import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft, Pin, PinOff, Archive, Trash2, Search } from 'lucide-react';
import { format } from 'date-fns';
import SEO from '@/components/SEO';

type PostRow = {
  id: string;
  content: string;
  user_id: string;
  thrive_circle_id: string | null;
  is_pinned: boolean | null;
  is_archived: boolean | null;
  created_at: string;
  post_type: string | null;
};

export default function CommunityPostsPage() {
  const { circleId } = useParams<{ circleId: string }>();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<PostRow[]>([]);
  const [circleName, setCircleName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; post: PostRow | null }>({ open: false, post: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (circleId) { fetchPosts(); fetchCircleName(); }
  }, [circleId]);

  const fetchCircleName = async () => {
    const { data } = await supabase.from('ThriveCircle').select('name').eq('id', circleId!).single();
    if (data) setCircleName(data.name || 'Unknown');
  };

  const fetchPosts = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('community_posts')
      .select('*')
      .eq('thrive_circle_id', circleId!)
      .order('created_at', { ascending: false });
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else setPosts(data || []);
    setIsLoading(false);
  };

  const togglePin = async (post: PostRow) => {
    const { error } = await supabase.from('community_posts').update({ is_pinned: !post.is_pinned }).eq('id', post.id);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { setPosts(prev => prev.map(p => p.id === post.id ? { ...p, is_pinned: !post.is_pinned } : p)); }
  };

  const archivePost = async (post: PostRow) => {
    const { error } = await supabase.from('community_posts').update({ is_archived: true }).eq('id', post.id);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Post archived' }); fetchPosts(); }
  };

  const deletePost = async () => {
    if (!deleteDialog.post) return;
    setIsSubmitting(true);
    const { error } = await supabase.from('community_posts').delete().eq('id', deleteDialog.post.id);
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Post deleted' }); fetchPosts(); }
    setDeleteDialog({ open: false, post: null });
    setIsSubmitting(false);
  };

  const filtered = posts.filter(p => p.content.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <SEO title={`Posts — ${circleName}`} noindex />
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/communities')}>
            <ArrowLeft className="h-4 w-4 mr-1" />Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Posts</h1>
            <p className="text-muted-foreground mt-1">Managing posts in <strong>{circleName}</strong></p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search posts..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No posts found.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Content</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(post => (
                    <TableRow key={post.id}>
                      <TableCell className="max-w-[300px] truncate">{post.content}</TableCell>
                      <TableCell className="font-mono text-xs">{post.user_id.slice(0, 12)}…</TableCell>
                      <TableCell>{post.post_type ? <Badge variant="secondary">{post.post_type}</Badge> : '—'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {post.is_pinned && <Badge className="bg-blue-100 text-blue-800">Pinned</Badge>}
                          {post.is_archived && <Badge variant="outline">Archived</Badge>}
                          {!post.is_pinned && !post.is_archived && <span className="text-muted-foreground text-sm">Active</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{format(new Date(post.created_at), 'MMM d, yyyy')}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" title={post.is_pinned ? 'Unpin' : 'Pin'} onClick={() => togglePin(post)}>
                            {post.is_pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                          </Button>
                          {!post.is_archived && (
                            <Button size="sm" variant="ghost" title="Archive" onClick={() => archivePost(post)}>
                              <Archive className="h-4 w-4" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-destructive" title="Delete" onClick={() => setDeleteDialog({ open: true, post })}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={deleteDialog.open} onOpenChange={open => !open && setDeleteDialog({ open: false, post: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Post</DialogTitle>
            <DialogDescription>This will permanently delete this post. This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, post: null })}>Cancel</Button>
            <Button variant="destructive" onClick={deletePost} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
